import React, { useEffect, useState } from "react";
import DrawerLayout from "../..";
import { UnitIcon } from "../../../../layout/Components/icons";
import Input from "../../../form/input";
import Select from "../../../select";
import "./style.scss";
import {
  useCreateUnitGroup,
  useMembers,
  useUpdateUnitGroup,
  type UnitGroupMutationRequest,
} from "../../../../api/unitGroups";
import { useCallTypes } from "../../../../hooks/useCallTypes";
import { useDepots } from "../../../../hooks/useDepots";
import { message } from "antd";

interface AssignUnitGroupProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: string;
  unit?: Unit | null;
  mode: "add" | "edit";
  onSave?: (data: Unit) => void;
  refetchUnit: () => void;
}
export type Unit = {
  depot_name: any;
  record: any;
  pikitup_unit_group_id(arg0: string, pikitup_unit_group_id: any): unknown;
  status_flg: number;
  unit_group_name: any;
  reference_no: any;
  escalated_members: any;
  created_date: string;
  unit_group_desc: string;
  id?: number;
  name: string;
  callType: number[];
  members: number[];
  depot: string | null;
  owners: number[];
  refetchUnit?: () => void;
  description: string;
};
const defaultFormData: Unit = {
  record: {},
  pikitup_unit_group_id: () => {},
  status_flg: 0,
  unit_group_name: "",
  reference_no: "",
  escalated_members: [],
  created_date: "",
  unit_group_desc: "",
  id: undefined,
  name: "",
  callType: [],
  members: [],
  depot: null,
  owners: [],
  description: "",
  depot_name: undefined,
};

interface CallTypeOption {
  label: string;
  value: number;
}
interface MemberOption {
  label: string;
  value: number;
}

const AssignUnitGroup: React.FC<AssignUnitGroupProps> = ({
  open,
  setOpen,
  title = "Add unit group",
  unit,
  mode,
  onSave,
  refetchUnit,
}) => {
  const [formData, setFormData] = useState<Unit>(defaultFormData);

  const createMutation = useCreateUnitGroup();
  const updateMutation = useUpdateUnitGroup();

  const { data: callTypes = [] } = useCallTypes();
  const { data: depots = [] } = useDepots();
  const { data: members = [] } = useMembers();

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (mode === "edit" && unit) {
      const depotId =
        unit?.record?.pikitup_depot_id ??
        unit?.record?.depot_id ??
        unit?.depot ??
        null;

      setFormData({
        ...defaultFormData,
        ...unit,
        name: unit?.name ?? unit?.record?.unit_group_name ?? "",
        callType: Array.isArray(unit?.record?.call_types)
          ? unit.record.call_types.map((c: any) => c.service_id)
          : [],
        members: Array.isArray(unit?.record?.members)
          ? unit.record.members.map((m: any) => m.pikitup_member_id)
          : [],
        depot: depotId !== null ? depotId.toString() : null,
        owners: Array.isArray(unit?.record?.escalated_members)
          ? unit.record.escalated_members.map((m: any) => m.pikitup_member_id)
          : [],
        description: unit.record?.unit_group_desc || "",
      });
    } else if (mode === "add") {
      setFormData(defaultFormData);
    }
  }, [mode, unit, open]);

  // const defaultCallTypeOptions: CallTypeOption[] = [
  //   { label: "Street cleaning", value: 30016 },
  //   { label: "Missed collection", value: 30019 },
  //   { label: "Property damaged(pickitup) (domestic)", value: 30020 },
  //   { label: "Illegal dumping", value: 30021 },
  //   { label: "Dead animal removal", value: 30006 },
  // ];
  const callTypeOptions: CallTypeOption[] =
    callTypes?.map((service: any) => ({
      label: service?.call_type || service?.service_desc || "Unknown",
      value: service.service_id,
    })) || [];

  const membersOptions: MemberOption[] = members.map((m) => ({
    label: `${m.member_first_name} ${m.member_surname}`,
    value: m.pikitup_member_id,
  }));

  const handleConfirm = async () => {
    try {
      if (!formData.name.trim()) {
        messageApi.warning("Please enter a group name.");
        return;
      }
      if (!formData.depot?.length || formData.depot === "null") {
        messageApi.warning("Please select a depot.");
        return;
      }
      if (formData.callType.length === 0) {
        messageApi.warning("Please select at least one call type.");
        return;
      }
      if (formData.members.length === 0) {
        messageApi.warning("Please select at least one member.");
        return;
      }

      const finalFormData = {
        ...formData,
        callType: formData.callType.map(Number),
        members: formData.members.map(Number),
        depot: formData.depot ? Number(formData.depot) : undefined,
      };

      let requestData: UnitGroupMutationRequest;

      if (mode === "add") {
        requestData = {
          action: "create",
          unit_group_name: finalFormData.name.trim(),
          service_ids: finalFormData.callType,
          member_ids: finalFormData.members,
          unit_group_description: finalFormData.description?.trim() || "",
          depot_id: finalFormData.depot,
          escalation_member_ids: finalFormData.owners,
        };
        await createMutation.mutateAsync(requestData);
        messageApi.success("Unit group created successfully!");
      } else if (
        mode === "edit" &&
        (unit?.id || unit?.record.pikitup_unit_group_id)
      ) {
        requestData = {
          action: "update",
          unit_group_id: unit.id ?? unit?.record.pikitup_unit_group_id,
          unit_group_name: finalFormData.name.trim(),
          service_ids: finalFormData.callType,
          member_ids: finalFormData.members,
          unit_group_description: finalFormData.description?.trim() || "",
          depot_id: finalFormData.depot,
          escalation_member_ids: finalFormData.owners,
        };
        await updateMutation.mutateAsync(requestData);
        refetchUnit();
        messageApi.success("Unit group updated successfully!");
      }

      onSave?.(formData);

      setTimeout(() => setOpen(false), 500);
    } catch (error: any) {}
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const depotOptions =
    depots?.map((d) => ({
      label: d.depot_name,
      value: d.pikitup_depot_id.toString(),
    })) || [];

  return (
    <>
      {contextHolder}
      <DrawerLayout
        extra={<UnitIcon />}
        open={open}
        setOpen={setOpen}
        title={title}
        onConfirm={handleConfirm}
        confirmText="Confirm"
        cancelText="Cancel"
        loading={isLoading}
      >
        <div className="flex flex-col gap-6 p-4">
          <div>
            <label className="description mb-[16px]">Information</label>
            <div className="flex flex-col gap-5">
              <Input
                inputClassname="h-[48px]"
                className="mt-3"
                placeholder="Unit name"
                value={formData.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
              <Select
                label=""
                options={depotOptions}
                placeholder="Choose depot"
                value={formData.depot || undefined}
                onChange={(value) => setFormData({ ...formData, depot: value })}
              />
              <Select
                allowClear={false}
                label=""
                mode="multiple"
                options={callTypeOptions}
                placeholder="Choose call type(s)"
                value={formData.callType}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    callType: Array.isArray(value) ? value.map(Number) : [],
                  })
                }
              />
              <Select
                allowClear={false}
                label=""
                mode="multiple"
                showSearch
                options={membersOptions.map((m) => ({
                  ...m,
                  value: String(m.value),
                }))}
                placeholder="Choose members"
                value={formData.members.map(String)}
                onChange={(values) =>
                  setFormData({ ...formData, members: values?.map(Number) })
                }
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
          </div>

          <div>
            <label className="description mb-[16px]">Description</label>
            <textarea
              placeholder="Enter details about the unit..."
              className="text-area"
              rows={4}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </div>
        </div>
      </DrawerLayout>
    </>
  );
};

export default AssignUnitGroup;
